import { useState, useCallback, useEffect, useMemo } from 'react';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Pagination,
  LoadingSkeleton,
  Input,
  Select,
  Modal,
} from '@nexora/design-system';
import {
  useGoodsReceipts,
  useCreateGoodsReceipt,
  useReceiveGoods,
  useCompleteGoodsReceipt,
  usePurchaseOrders,
  type GoodsReceipt,
  type GoodsReceiptInput,
  type GoodsReceiptStatus,
  type ReceiveGoodsInput,
} from './useProcurement';
import { useWarehouses, isWarehouseActive } from '../inventory/useInventory';

/* `GoodsReceiptStatus` do dominio: DRAFT, COMPLETED, CANCELLED. O mapa antigo
   era em minusculas ('completed', 'partial', 'pending') e nenhuma das chaves
   casava — o selo mostrava o valor cru. */
const STATUS_LABEL: Record<GoodsReceiptStatus, string> = {
  DRAFT: 'Em conferência',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const STATUS_VARIANT: Record<GoodsReceiptStatus, 'success' | 'warning' | 'error'> = {
  DRAFT: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

/** Quanto do esperado já foi conferido. */
function conferido(receipt: GoodsReceipt): { recebidos: number; itens: number; divergentes: number } {
  let recebidos = 0;
  let divergentes = 0;
  for (const item of receipt.items) {
    if (item.receivedQuantity <= 0) continue;
    recebidos += 1;
    /* Divergencia so entre o que JA foi conferido: item ainda sem lancamento
       nao diverge do pedido, esta por conferir. */
    if (item.receivedQuantity !== item.expectedQuantity) divergentes += 1;
  }
  return { recebidos, itens: receipt.items.length, divergentes };
}

export function GoodsReceiptsPage(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data: receipts, isLoading, error } = useGoodsReceipts();
  const createMutation = useCreateGoodsReceipt();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: GoodsReceiptInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Recebimento aberto em conferência.', 'success');
        setModalOpen(false);
      } catch (err) {
        showToast((err as Error).message ?? 'Erro ao abrir recebimento.', 'error');
      }
    },
    [createMutation, showToast],
  );

  const receiptList = useMemo(() => receipts ?? [], [receipts]);
  const pagina = receiptList.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="gr-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Recebimentos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Conferência de mercadoria contra a ordem de compra aprovada.
          </p>
        </div>
        <Button variant="primary" onClick={() => setModalOpen(true)}>
          Novo recebimento
        </Button>
      </div>

      <Card>
        <CardHeader
          action={
            <span className="gr-count">
              {receiptList.length} recebimento{receiptList.length === 1 ? '' : 's'}
            </span>
          }
        >
          Conferências
        </CardHeader>

        {isLoading && (
          <div className="gr-page__loading">
            <LoadingSkeleton count={3} height="48px" />
            <span className="gr-page__loading-text">Carregando recebimentos...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="gr-page__error">
            <p className="gr-page__error-title">Erro ao carregar recebimentos</p>
            <p className="gr-page__error-detail">{(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && receiptList.length === 0 && (
          <div className="gr-page__empty">
            <p className="gr-page__empty-text">Nenhum recebimento encontrado.</p>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
              Abrir o primeiro recebimento
            </Button>
          </div>
        )}

        {!isLoading && !error && receiptList.length > 0 && (
          <div className="gr-page__table-wrap">
            <table className="gr-page__table">
              <thead>
                <tr>
                  {['Recebimento', 'Fornecedor', 'Nota', 'Conferência', 'Status', 'Ações'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagina.map((r) => {
                  const { recebidos, itens, divergentes } = conferido(r);
                  return (
                    <tr key={r.id} className="gr-row">
                      {/* O numero do recebimento e a identidade dele. A coluna
                          antiga era "OC" e lia `orderNumber`, campo que o DTO
                          nao publica: saia vazia em toda linha. */}
                      <td className="gr-cell--order">
                        {r.receiptNumber}
                        <small className="gr-cell__sub">{formatDate(r.createdAt)}</small>
                      </td>
                      <td>{r.supplierName}</td>
                      <td className="gr-cell--date">
                        {r.documentNumber ?? '—'}
                        {r.documentDate ? (
                          <small className="gr-cell__sub">{formatDate(r.documentDate)}</small>
                        ) : null}
                      </td>
                      <td className="gr-cell--items">
                        {recebidos} de {itens}
                        {divergentes > 0 ? (
                          <small className="gr-cell__sub gr-cell__sub--warn">
                            {divergentes} com divergência
                          </small>
                        ) : null}
                      </td>
                      <td className="gr-cell--status">
                        <Badge variant={STATUS_VARIANT[r.status]} size="sm" dot>
                          {STATUS_LABEL[r.status]}
                        </Badge>
                      </td>
                      <td className="gr-cell--actions">
                        <ReceiptDetail receipt={r} onToast={showToast} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && receiptList.length > pageSize && (
          <div className="gr-page__pagination">
            <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
              <Pagination
                page={page}
                totalPages={Math.ceil(receiptList.length / pageSize)}
                onChange={(p) => setPage(p)}
              />
            </div>
          </div>
        )}
      </Card>

      <CreateReceiptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
      />

      {toastMessage && (
        <div role="alert" className={`gr-toast gr-toast--${toastVariant}`}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar" className="gr-toast__close">
            &times;
          </button>
        </div>
      )}

      <style>{`
        .gr-page { padding: 1.5rem; }
        .gr-count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .gr-page__loading { display: flex; align-items: center; justify-content: center; padding: 3rem; gap: 0.75rem; color: #43474e; }
        .gr-page__loading-text { font-size: 0.875rem; }
        .gr-page__error { padding: 3rem 2rem; text-align: center; color: #ba1a1a; }
        .gr-page__error-title { font-weight: 600; margin: 0 0 0.5rem; }
        .gr-page__error-detail { font-size: 0.875rem; margin: 0; color: #74777f; }
        .gr-page__empty { padding: 3rem 2rem; text-align: center; color: #74777f; }
        .gr-page__empty-text { margin: 0 0 1rem; font-size: 0.875rem; }
        .gr-page__table-wrap { overflow-x: auto; }
        .gr-page__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .gr-page__table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .gr-page__table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .gr-row:hover td { background: #eceef0; }
        .gr-cell--order { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; white-space: nowrap; }
        .gr-cell--date { white-space: nowrap; }
        .gr-cell--items { white-space: nowrap; font-variant-numeric: tabular-nums; }
        .gr-cell--actions { white-space: nowrap; }
        .gr-cell__sub { display: block; color: #74777f; font-size: 12px; font-weight: 400; }
        .gr-cell__sub--warn { color: #633f0f; }
        .gr-page__pagination { border-top: 1px solid #eceef0; }
        .gr-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px; }
        .gr-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .gr-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .gr-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
        .gr-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.5rem; }
        .gr-detail-label { margin: 0; font-size: 0.75rem; color: #74777f; }
        .gr-detail-value { margin: 0.125rem 0 0; font-weight: 600; }
        .gr-detail-value--mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; font-size: 12px; }
        .gr-detail-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .gr-detail-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .gr-detail-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .gr-detail-table th.right, .gr-detail-table td.right { text-align: right; }
        .gr-hint { font-size: 12px; line-height: 16px; color: #43474e; margin: 0 0 0.75rem; }
        .gr-hint--warn { color: #633f0f; background: #ffddba; padding: 8px; border-radius: 4px; }
        .gr-form { display: flex; flex-direction: column; gap: 1rem; }
        .gr-order-picker { display: flex; gap: 0.5rem; align-items: flex-end; }
        .gr-order-picker-input { flex: 1; }
        .gr-order-info { padding: 0.75rem; background: #f2f4f6; border-radius: 4px; }
        .gr-order-info p { margin: 0; }
        .gr-order-info .supplier-name { font-size: 0.875rem; font-weight: 600; }
        .gr-order-info .meta { font-size: 0.75rem; color: #43474e; margin-top: 0.25rem; }
        .gr-qty-input { width: 90px; text-align: right; }
      `}</style>
    </div>
  );
}

/* ── Detalhe e conferência ────────────────────────────────────────────────── */

function ReceiptDetail({
  receipt,
  onToast,
}: {
  receipt: GoodsReceipt;
  onToast: (message: string, variant: 'success' | 'error') => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [quantidades, setQuantidades] = useState<Record<string, string>>({});
  const receber = useReceiveGoods();
  const concluir = useCompleteGoodsReceipt();

  const emConferencia = receipt.status === 'DRAFT';

  const abrir = (): void => {
    setQuantidades(
      Object.fromEntries(
        receipt.items.map((item) => [
          item.id,
          String(item.receivedQuantity > 0 ? item.receivedQuantity : item.expectedQuantity),
        ]),
      ),
    );
    setOpen(true);
  };

  /** Segundo passo do domínio: registrar o que chegou. */
  const registrar = async (): Promise<void> => {
    const items: ReceiveGoodsInput['items'] = [];
    for (const item of receipt.items) {
      const bruto = quantidades[item.id];
      const quantidade = Number((bruto ?? '').replace(',', '.'));
      if (!Number.isFinite(quantidade) || quantidade < 0) continue;
      items.push({ itemId: item.id, quantity: quantidade });
    }
    if (items.length === 0) {
      onToast('Informe a quantidade recebida de ao menos um item.', 'error');
      return;
    }
    try {
      await receber.mutateAsync({ id: receipt.id, items });
      onToast('Quantidades registradas.', 'success');
    } catch (err) {
      onToast((err as Error).message ?? 'Erro ao registrar quantidades.', 'error');
    }
  };

  const fechar = async (): Promise<void> => {
    try {
      await concluir.mutateAsync(receipt.id);
      onToast('Recebimento concluído.', 'success');
      setOpen(false);
    } catch (err) {
      onToast((err as Error).message ?? 'Erro ao concluir recebimento.', 'error');
    }
  };

  const footer = emConferencia ? (
    <>
      <Button variant="outline" onClick={() => setOpen(false)}>
        Fechar
      </Button>
      <Button variant="secondary" loading={receber.isPending} onClick={() => void registrar()}>
        Registrar quantidades
      </Button>
      <Button variant="primary" loading={concluir.isPending} onClick={() => void fechar()}>
        Concluir recebimento
      </Button>
    </>
  ) : (
    <Button variant="outline" onClick={() => setOpen(false)}>
      Fechar
    </Button>
  );

  return (
    <>
      <Button variant="ghost" size="sm" onClick={abrir}>
        {emConferencia ? 'Conferir' : 'Detalhes'}
      </Button>
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={`Recebimento ${receipt.receiptNumber}`}
        footer={footer}
      >
        <div className="gr-detail-grid">
          <div>
            <p className="gr-detail-label">Fornecedor</p>
            <p className="gr-detail-value">{receipt.supplierName}</p>
          </div>
          <div>
            <p className="gr-detail-label">Recebido por</p>
            <p className="gr-detail-value">{receipt.receivedBy || '—'}</p>
          </div>
          <div>
            <p className="gr-detail-label">Nota</p>
            <p className="gr-detail-value">
              {receipt.documentNumber ?? '—'}
              {receipt.documentDate ? ` · ${formatDate(receipt.documentDate)}` : ''}
            </p>
          </div>
          <div>
            <p className="gr-detail-label">Status</p>
            <p className="gr-detail-value">
              <Badge variant={STATUS_VARIANT[receipt.status]} size="sm" dot>
                {STATUS_LABEL[receipt.status]}
              </Badge>
            </p>
          </div>
          <div>
            {/* O DTO publica o id do pedido, nao o numero dele. Mostrar o id
                é menos útil que inventar um número, mas é o que existe. */}
            <p className="gr-detail-label">Ordem de compra</p>
            <p className="gr-detail-value gr-detail-value--mono">{receipt.purchaseOrderId}</p>
          </div>
          <div>
            <p className="gr-detail-label">Frete e outros custos</p>
            <p className="gr-detail-value">
              {formatCurrency(receipt.freightCost + receipt.otherCosts)}
            </p>
          </div>
        </div>

        <h3 className="font-headline-sm text-headline-sm text-on-surface mt-0 mb-sm">Itens</h3>
        {emConferencia ? (
          <p className="gr-hint">
            O recebimento nasce com as quantidades <strong>esperadas</strong> do
            pedido. Ajuste o que chegou de fato e registre — a divergência fica
            gravada por item.
          </p>
        ) : null}
        <div style={{ overflowX: 'auto' }}>
          <table className="gr-detail-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th className="right">Esperado</th>
                <th className="right">Recebido</th>
                <th className="right">Divergência</th>
                <th className="right">Custo unit.</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((item) => {
                /* `expectedQuantity` — era lido como `orderedQuantity`, que nao
                   existe, e a diferenca dava NaN em toda linha. */
                const diff = item.receivedQuantity - item.expectedQuantity;
                return (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 500 }}>
                      {item.productName}
                      {item.sku ? <small className="gr-cell__sub">{item.sku}</small> : null}
                    </td>
                    <td className="right">{item.expectedQuantity}</td>
                    <td className="right" style={{ fontWeight: 600 }}>
                      {emConferencia ? (
                        <Input
                          aria-label={`Quantidade recebida de ${item.productName}`}
                          className="gr-qty-input"
                          type="number"
                          min={0}
                          step="0.001"
                          value={quantidades[item.id] ?? ''}
                          onChange={(e) =>
                            setQuantidades((atual) => ({ ...atual, [item.id]: e.target.value }))
                          }
                        />
                      ) : (
                        item.receivedQuantity
                      )}
                    </td>
                    <td className="right">
                      {diff === 0 ? (
                        <span style={{ color: '#2e7d32' }}>OK</span>
                      ) : (
                        <Badge variant={diff < 0 ? 'warning' : 'error'} size="sm">
                          {diff > 0 ? `+${diff}` : String(diff)}
                        </Badge>
                      )}
                    </td>
                    <td className="right">{formatCurrency(item.unitCost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}

/* ── Abertura do recebimento ───────────────────────────────────────────────── */

export function CreateReceiptModal({
  isOpen,
  onClose,
  onSubmit,
  submitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: GoodsReceiptInput) => Promise<void>;
  submitting: boolean;
}): JSX.Element {
  const [orderId, setOrderId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentDate, setDocumentDate] = useState('');

  const { data: ordersData } = usePurchaseOrders({ page: 1, limit: 100, status: 'approved' });
  const { data: warehouses } = useWarehouses();

  const orders = ordersData?.data ?? [];
  const selectedOrder = orders.find((o) => o.id === orderId);

  const armazensAtivos = useMemo(
    () => (warehouses ?? []).filter(isWarehouseActive),
    [warehouses],
  );

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedOrder || !warehouseId) return;

    /* `CreateGoodsReceiptDto` por inteiro. A versao anterior mandava
       `{ orderId, items: [{ productId, receivedQuantity }] }` — com
       `forbidNonWhitelisted: true` o servidor respondia 400, e o formulario
       nunca funcionou. */
    await onSubmit({
      purchaseOrderId: selectedOrder.id,
      supplierId: selectedOrder.vendorId,
      supplierName: selectedOrder.vendorName,
      warehouseId,
      items: selectedOrder.items.map((item) => ({
        purchaseOrderItemId: item.id,
        productId: item.productId,
        productName: item.description,
        expectedQuantity: item.quantity,
        unitCost: item.unitValue,
      })),
      ...(documentNumber ? { documentNumber } : {}),
      ...(documentDate ? { documentDate } : {}),
    });
  };

  const valid = Boolean(selectedOrder && warehouseId && selectedOrder.items.length > 0);

  const footer = (
    <>
      <Button variant="outline" type="button" onClick={onClose}>
        Cancelar
      </Button>
      <Button variant="primary" type="submit" form="create-gr-form" loading={submitting} disabled={!valid}>
        Abrir conferência
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo recebimento" footer={footer}>
      <form id="create-gr-form" onSubmit={(e) => void handleSubmit(e)}>
        <div className="gr-form">
          <p className="gr-hint">
            O recebimento é aberto em conferência com as quantidades esperadas do
            pedido. O que chegou de fato se registra depois, item por item.
          </p>

          <Select
            label="Ordem de compra aprovada"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            required
          >
            <option value="">Selecione…</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.number} — {o.vendorName} ({formatCurrency(o.total)})
              </option>
            ))}
          </Select>

          {orders.length === 0 && (
            <p className="gr-hint gr-hint--warn">
              Nenhuma ordem de compra aprovada. O recebimento só existe contra um
              pedido aprovado.
            </p>
          )}

          <Select
            label="Armazém de entrada"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            required
          >
            <option value="">Selecione…</option>
            {armazensAtivos.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </Select>

          {armazensAtivos.length === 0 && (
            <p className="gr-hint gr-hint--warn">
              Nenhum armazém ativo. O destino da mercadoria é obrigatório.
            </p>
          )}

          <Input
            label="Número da nota (opcional)"
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="Ex.: 123456"
          />
          <Input
            label="Data da nota (opcional)"
            type="date"
            value={documentDate}
            onChange={(e) => setDocumentDate(e.target.value)}
          />

          {selectedOrder && (
            <>
              <div className="gr-order-info">
                {/* Campos do DTO real: o pedido tem `vendorName`, `number` e
                    `createdAt`. */}
                <p className="supplier-name">{selectedOrder.vendorName}</p>
                <p className="meta">
                  {selectedOrder.number} · Total: {formatCurrency(selectedOrder.total)} · Emissão:{' '}
                  {formatDate(selectedOrder.createdAt)}
                </p>
              </div>

              <div>
                <span
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#191c1e',
                    display: 'block',
                    marginBottom: '0.5rem',
                  }}
                >
                  Itens esperados ({selectedOrder.items.length})
                </span>
                <div style={{ overflowX: 'auto' }}>
                  <table className="gr-detail-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th className="right">Qtd. pedida</th>
                        <th className="right">Custo unit.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td className="right">{item.quantity}</td>
                          <td className="right">{formatCurrency(item.unitValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}
